import React, { useMemo, useState } from 'react';
import {
  AlertTriangle,
  ExternalLink,
  FileCheck2,
  Link2,
  PackageCheck,
  RefreshCw,
  Unlink,
} from 'lucide-react';

import {
  buildGenerationBomBasis,
  evaluateGenerationBomBasisAlignment,
} from '../../../lib/generationImpact/generationBomBasis';
import {
  resolveBomItemImport,
  type GenerationEquipmentImportTier,
} from '../../../lib/generationImpact/equipmentImportSources';
import type { GenerationBomBasis } from '../../../types/generationImpact';
import type { SupplyChainPlan } from '../../../types/supplyChain';

interface GenerationBomBasisBoardProps {
  plan?: SupplyChainPlan;
  basis?: GenerationBomBasis;
  readOnly?: boolean;
  actionsDisabled?: boolean;
  onLink: (basis: GenerationBomBasis) => void;
  onClear: () => void;
  onOpenBom?: () => void;
  /**
   * Route this LIVE plan line into the equipment selection (guided import).
   * Starts the import only — this board still never writes solver inputs.
   */
  onUseInEquipmentSelection?: (bomItemId: string) => void;
}

const IMPORT_TIER_LABELS: Record<GenerationEquipmentImportTier, string> = {
  'catalog-envelope': 'Real envelope',
  'prefill-full': 'Prefill · full data',
  'prefill-partial': 'Prefill · partial',
  insufficient: 'Needs data',
};

function compactNumber(value: number | null): string {
  if (value == null) return 'Not entered';
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value);
}

function money(value: number | null): string {
  if (value == null) return 'Unpriced';
  if (value >= 1_000_000_000) return `$${(value / 1_000_000_000).toFixed(1)}B`;
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function date(value: string): string {
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? value : parsed.toLocaleDateString();
}

function evidenceDocumentCount(basis: GenerationBomBasis): number {
  return new Set(basis.items.flatMap(item => item.evidenceDocumentIds)).size;
}

function signedDelta(value: number): string {
  return `${value > 0 ? '+' : ''}${new Intl.NumberFormat('en-US', { maximumFractionDigits: 1 }).format(value)}`;
}

/**
 * Procurement provenance for Generation & Impact. This surface deliberately
 * never writes solver inputs: BOM power is net nameplate, while scenario unit
 * duty is ISO gross and must remain catalog/project-duty controlled.
 */
export function GenerationBomBasisBoard({
  plan,
  basis,
  readOnly = false,
  actionsDisabled = false,
  onLink,
  onClear,
  onOpenBom,
  onUseInEquipmentSelection,
}: GenerationBomBasisBoardProps): React.JSX.Element {
  const [error, setError] = useState<string | null>(null);
  // Import tier per LIVE plan line (frozen basis items carry no OEM snapshot,
  // so resolution always reads the current plan; missing lines get no action).
  const liveImportTiers = useMemo(
    () => new Map((plan?.bom ?? []).map(item => [item.id, resolveBomItemImport(item).tier] as const)),
    [plan],
  );
  const live = useMemo(() => {
    if (!plan) return { basis: null, error: 'No canonical supply-chain plan is available.' };
    try {
      return { basis: buildGenerationBomBasis(plan), error: null };
    } catch (caught) {
      return { basis: null, error: (caught as Error).message };
    }
  }, [plan]);
  const alignment = useMemo(
    () => basis ? evaluateGenerationBomBasisAlignment(basis, plan) : null,
    [basis, plan],
  );
  // Once linked, the scenario's immutable basis is the primary record. The
  // live plan is shown separately only as a refresh candidate.
  const displayBasis = basis ?? live.basis ?? null;
  const liveDiffers = Boolean(
    basis
      && live.basis
      && alignment?.alignmentStatus !== 'aligned',
  );
  const disabled = readOnly || actionsDisabled;
  const status = alignment?.alignmentStatus ?? 'unlinked';
  const statusTone = status === 'aligned'
    ? 'border-success/40 bg-success/10 text-success'
    : status === 'modified' || status === 'missing'
      ? 'border-warning/40 bg-warning/10 text-warning'
      : 'border-border bg-background text-text-muted';

  const link = () => {
    if (!live.basis) return;
    try {
      setError(null);
      onLink(live.basis);
    } catch (caught) {
      setError((caught as Error).message);
    }
  };

  return (
    <section
      aria-label="Generation BOM linkage"
      className="min-w-0 max-w-full border-2 border-border bg-card"
      data-testid="generation-bom-basis-board"
    >
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-3 py-3 sm:px-4">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider">
            <PackageCheck className="h-4 w-4 shrink-0 text-primary" />
            Equipment BOM evidence
          </h2>
          <p className="mt-1 max-w-4xl text-[10px] text-text-muted">
            Freeze the active generation procurement selection into this scenario, including quantities, status, commercial basis, lead time, quotes, and linked evidence.
          </p>
        </div>
        <span className={`border px-2 py-1 font-mono text-[9px] font-bold uppercase tracking-wider ${statusTone} rounded-md`} data-testid="generation-bom-alignment-status">
          {status === 'unlinked' ? 'Not linked' : status}
        </span>
      </div>

      <div className="flex items-start gap-2 border-b border-primary/30 bg-primary/[0.05] px-3 py-2.5 text-[10px] text-text-secondary sm:px-4" role="note">
        <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
        <p>
          <strong className="text-text-primary">Reference only — calculations stay unchanged.</strong>{' '}
          BOM MW is net nameplate. Generation &amp; Impact uses ISO-gross equipment duty, ambient correction, auxiliaries, and contingency groups; linking never converts one basis into the other.
        </p>
      </div>

      {displayBasis ? (
        <>
          <div
            className="flex flex-wrap items-center justify-between gap-2 border-b border-border bg-background px-3 py-2 text-[9px] text-text-muted sm:px-4"
            data-testid={basis ? 'generation-bom-frozen-basis' : 'generation-bom-live-preview'}
          >
            <strong className="font-bold uppercase tracking-wider text-text-primary">
              {basis ? 'Frozen in this scenario' : 'Current active BOM preview'}
            </strong>
            <span>
              Plan v{displayBasis.sourcePlanVersion} · {date(displayBasis.sourcePlanUpdatedAt)}
              {basis ? ` · ${basis.basisHash.slice(0, 12)}…` : ' · link to freeze this reference'}
            </span>
          </div>
          <div className="grid gap-px border-b border-border bg-border text-[10px] sm:grid-cols-2 lg:grid-cols-4">
            <div className="bg-card p-3"><span className="block text-text-muted">Active generation lines</span><strong className="mt-1 block text-lg tabular-nums">{displayBasis.items.length}</strong></div>
            <div className="bg-card p-3"><span className="block text-text-muted">BOM net nameplate</span><strong className="mt-1 block text-lg tabular-nums">{compactNumber(displayBasis.totalNetNameplateMW)}{displayBasis.totalNetNameplateMW == null ? '' : ' MW'}</strong></div>
            <div className="bg-card p-3"><span className="block text-text-muted">Evidence documents</span><strong className="mt-1 block text-lg tabular-nums">{evidenceDocumentCount(displayBasis)}</strong></div>
            <div className="bg-card p-3"><span className="block text-text-muted">Canonical plan</span><strong className="mt-1 block">v{displayBasis.sourcePlanVersion} · {date(displayBasis.sourcePlanUpdatedAt)}</strong></div>
          </div>

          <div className="divide-y divide-border" data-testid="generation-bom-items">
            {displayBasis.items.map(item => (
              <article className="grid min-w-0 grid-cols-1 gap-2 px-3 py-3 text-[10px] sm:grid-cols-[minmax(0,1.7fr)_repeat(3,minmax(7rem,0.7fr))] sm:items-center sm:px-4" key={item.bomItemId}>
                <div className="min-w-0">
                  <h3 className="truncate font-bold text-text-primary" title={item.name}>{item.name}</h3>
                  <p className="mt-0.5 truncate text-[9px] text-text-muted" title={item.spec}>{item.spec || 'No specification entered'}</p>
                  <div className="mt-1 flex flex-wrap items-center gap-1">
                    <span className="border border-border px-1.5 py-0.5 font-mono text-[10px] uppercase text-text-muted rounded-sm">{item.status}</span>
                    {item.phaseId && <span className="border border-border px-1.5 py-0.5 text-[10px] text-text-muted rounded-md">{item.phaseId}</span>}
                    {item.optionGroupId && <span className="border border-primary/30 px-1.5 py-0.5 text-[10px] text-primary rounded-md">Selected option</span>}
                    {onUseInEquipmentSelection && !readOnly && liveImportTiers.has(item.bomItemId) && (
                      <>
                        <span className="border border-border px-1.5 py-0.5 text-[10px] uppercase text-text-muted rounded-sm">
                          {IMPORT_TIER_LABELS[liveImportTiers.get(item.bomItemId)!]}
                        </span>
                        <button
                          type="button"
                          disabled={actionsDisabled}
                          onClick={() => onUseInEquipmentSelection(item.bomItemId)}
                          className="border border-primary/50 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider text-primary hover:bg-primary/5 disabled:border-border disabled:text-text-muted rounded-sm"
                          data-testid={`generation-bom-use-in-selection-${item.bomItemId}`}
                        >
                          Use in equipment selection
                        </button>
                      </>
                    )}
                  </div>
                </div>
                <div><span className="block text-[10px] uppercase tracking-wider text-text-muted">Quantity</span><strong className="mt-0.5 block tabular-nums">{compactNumber(item.quantity)} {item.unit}</strong></div>
                <div><span className="block text-[10px] uppercase tracking-wider text-text-muted">Net nameplate</span><strong className="mt-0.5 block tabular-nums">{compactNumber(item.totalNetNameplateMW)}{item.totalNetNameplateMW == null ? '' : ' MW'}</strong></div>
                <div><span className="block text-[10px] uppercase tracking-wider text-text-muted">CAPEX / lead</span><strong className="mt-0.5 block">{money(item.totalCostUsd)} · {compactNumber(item.leadTimeMonthsLow)}–{compactNumber(item.leadTimeMonthsHigh)} mo</strong><span className="mt-0.5 block text-[10px] text-text-muted"><FileCheck2 className="mr-1 inline h-3 w-3" />{item.evidenceDocumentIds.length} evidence</span></div>
              </article>
            ))}
          </div>
        </>
      ) : (
        <div className="px-3 py-5 sm:px-4">
          <p className="text-[10px] font-bold text-text-primary">No generation equipment is available to link.</p>
          <p className="mt-1 text-[10px] text-text-muted">{live.error}</p>
        </div>
      )}

      {basis && live.basis && liveDiffers && (
        <div
          className="border-t border-warning/30 bg-warning/5 px-3 py-3 text-[10px] sm:px-4"
          data-testid="generation-bom-current-active-delta"
        >
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <strong className="block font-bold uppercase tracking-wider text-text-primary">Current active BOM</strong>
              <span className="mt-0.5 block text-[9px] text-text-muted">Refresh candidate only · never a solver input</span>
            </div>
            <span className="font-mono text-[9px] text-warning">Plan v{live.basis.sourcePlanVersion} · {date(live.basis.sourcePlanUpdatedAt)}</span>
          </div>
          <div className="mt-2 grid gap-px bg-border sm:grid-cols-3">
            <div className="bg-card px-3 py-2"><span className="block text-text-muted">Generation lines</span><strong className="mt-0.5 block">{live.basis.items.length} <small className="font-normal text-text-muted">({signedDelta(live.basis.items.length - basis.items.length)})</small></strong></div>
            <div className="bg-card px-3 py-2"><span className="block text-text-muted">Net-nameplate reference</span><strong className="mt-0.5 block">{compactNumber(live.basis.totalNetNameplateMW)}{live.basis.totalNetNameplateMW == null ? '' : ' MW'} <small className="font-normal text-text-muted">{live.basis.totalNetNameplateMW != null && basis.totalNetNameplateMW != null ? `(${signedDelta(live.basis.totalNetNameplateMW - basis.totalNetNameplateMW)} MW)` : '(not comparable)'}</small></strong></div>
            <div className="bg-card px-3 py-2"><span className="block text-text-muted">Evidence documents</span><strong className="mt-0.5 block">{evidenceDocumentCount(live.basis)} <small className="font-normal text-text-muted">({signedDelta(evidenceDocumentCount(live.basis) - evidenceDocumentCount(basis))})</small></strong></div>
          </div>
          <p className="mt-2 text-[9px] leading-relaxed text-text-muted">Refresh BOM link to replace the frozen procurement reference on the next scenario save. ISO-gross duty, derates, auxiliaries, and every calculated result remain unchanged.</p>
        </div>
      )}

      {(alignment?.alignmentNotes.length || error) ? (
        <div className="border-t border-warning/30 bg-warning/5 px-3 py-2 text-[9px] text-warning sm:px-4" role="alert">
          {[...(alignment?.alignmentNotes ?? []), ...(error ? [error] : [])].join(' ')}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border bg-background px-3 py-2.5 sm:px-4">
        <p className="min-w-0 text-[9px] text-text-muted">
          {basis ? `Frozen basis ${basis.basisHash.slice(0, 12)}…` : 'Linking adds a hash-bound procurement snapshot to the next scenario save.'}
        </p>
        <div className="flex flex-wrap items-center gap-1.5">
          {onOpenBom && (
            <button className="inline-flex items-center gap-1 border border-border px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-primary hover:text-primary rounded-md" onClick={onOpenBom} type="button">
              Open Equipment BOM <ExternalLink className="h-3 w-3" />
            </button>
          )}
          {basis && (
            <button className="inline-flex items-center gap-1 border border-border px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-text-secondary hover:border-danger hover:text-danger disabled:opacity-50 rounded-md" disabled={disabled} onClick={onClear} type="button">
              <Unlink className="h-3 w-3" /> Unlink
            </button>
          )}
          <button
            className="inline-flex items-center gap-1 border border-primary bg-primary px-2 py-1.5 text-[9px] font-bold uppercase tracking-wider text-primary-foreground hover:bg-primary/90 disabled:cursor-not-allowed disabled:border-border disabled:bg-transparent disabled:text-text-muted rounded-md"
            disabled={disabled || !live.basis || alignment?.alignmentStatus === 'aligned'}
            onClick={link}
            type="button"
          >
            {basis ? <RefreshCw className="h-3 w-3" /> : <Link2 className="h-3 w-3" />}
            {basis ? 'Refresh BOM link' : 'Link active BOM'}
          </button>
        </div>
      </div>
    </section>
  );
}
